import type { DrivingChallengeId } from '../data/challengeOptions';
import type { DrivingCourseKind } from '../data/courseOptions';
import type { ChallengeStatus, DrivingChallengeState } from './drivingChallenges';
import type { DrivingSessionState } from './drivingSession';
import type { VehicleInput, VehicleState } from './vehicleDriving';

const SAMPLE_INTERVAL_SECONDS = 0.1;
const MAX_SAMPLES_PER_RUN = 2400;

export interface DrivingTelemetrySample {
  /** 시도 시작 뒤 경과 시간(초). */
  timeSeconds: number;
  speedKmh: number;
  throttle: number;
  brake: number;
  longitudinalG: number;
  lateralG: number;
  slip: number;
  powerKw: number;
}

export type DrivingTelemetryRunKind = 'course' | 'challenge';

export interface CompletedDrivingTelemetryRun {
  revision: number;
  vehicleId: string;
  kind: DrivingTelemetryRunKind;
  activityId: string;
  durationSeconds: number;
  resultValue: number;
  samples: readonly DrivingTelemetrySample[];
}

interface TelemetryChannel {
  active: boolean;
  elapsedSeconds: number;
  sampleAccumulator: number;
  samples: DrivingTelemetrySample[];
}

export interface DrivingTelemetryState {
  vehicleId: string;
  courseId: string;
  challengeId: DrivingChallengeId;
  observedSpawnCount: number;
  course: TelemetryChannel;
  challenge: TelemetryChannel;
  completedRun: CompletedDrivingTelemetryRun | null;
  revision: number;
}

function createChannel(): TelemetryChannel {
  return { active: false, elapsedSeconds: 0, sampleAccumulator: 0, samples: [] };
}

export function createDrivingTelemetryState(
  vehicleId: string,
  courseId: string,
  challengeId: DrivingChallengeId,
  spawnCount = 0
): DrivingTelemetryState {
  return {
    vehicleId,
    courseId,
    challengeId,
    observedSpawnCount: spawnCount,
    course: createChannel(),
    challenge: createChannel(),
    completedRun: null,
    revision: 0,
  };
}

function makeSample(
  timeSeconds: number,
  vehicle: VehicleState,
  input: VehicleInput
): DrivingTelemetrySample {
  return {
    timeSeconds,
    speedKmh: Math.abs(vehicle.speed) * 3.6,
    throttle: input.throttle,
    brake: input.brake,
    longitudinalG: vehicle.longitudinalAcceleration / 9.81,
    // a_lat = v · yaw rate. 부호를 보존해서 좌/우 하중을 그래프에서 구분합니다.
    lateralG: (vehicle.speed * vehicle.yawRate) / 9.81,
    slip: vehicle.slipIntensity,
    powerKw: vehicle.powerKw,
  };
}

function beginChannel(vehicle: VehicleState, input: VehicleInput): TelemetryChannel {
  return {
    active: true,
    elapsedSeconds: 0,
    sampleAccumulator: 0,
    samples: [makeSample(0, vehicle, input)],
  };
}

function advanceChannel(
  channel: TelemetryChannel,
  dt: number,
  vehicle: VehicleState,
  input: VehicleInput
): void {
  if (!channel.active) {
    return;
  }
  const acceptedDt = Math.min(Math.max(dt, 0), 1 / 20);
  channel.elapsedSeconds += acceptedDt;
  channel.sampleAccumulator += acceptedDt;

  while (
    channel.sampleAccumulator + 1e-9 >= SAMPLE_INTERVAL_SECONDS
    && channel.samples.length < MAX_SAMPLES_PER_RUN
  ) {
    channel.sampleAccumulator -= SAMPLE_INTERVAL_SECONDS;
    const sampleTime = channel.elapsedSeconds - channel.sampleAccumulator;
    channel.samples.push(makeSample(sampleTime, vehicle, input));
  }
}

function withFinalSample(
  channel: TelemetryChannel,
  vehicle: VehicleState,
  input: VehicleInput
): readonly DrivingTelemetrySample[] {
  const last = channel.samples[channel.samples.length - 1];
  if (last === undefined || Math.abs(last.timeSeconds - channel.elapsedSeconds) > 1e-4) {
    channel.samples.push(makeSample(channel.elapsedSeconds, vehicle, input));
  }
  return [...channel.samples];
}

function publishRun(
  state: DrivingTelemetryState,
  kind: DrivingTelemetryRunKind,
  activityId: string,
  resultValue: number,
  channel: TelemetryChannel,
  vehicle: VehicleState,
  input: VehicleInput
): void {
  state.revision += 1;
  state.completedRun = Object.freeze({
    revision: state.revision,
    vehicleId: state.vehicleId,
    kind,
    activityId,
    durationSeconds: channel.elapsedSeconds,
    resultValue,
    samples: Object.freeze(withFinalSample(channel, vehicle, input)),
  });
}

export interface DrivingTelemetryFrame {
  previousVehicle: VehicleState;
  nextVehicle: VehicleState;
  input: VehicleInput;
  dt: number;
  vehicleId: string;
  courseId: string;
  courseKind: DrivingCourseKind;
  sessionWasActive: boolean;
  completedLapsBefore: number;
  session: DrivingSessionState;
  challengeId: DrivingChallengeId;
  challengeStatusBefore: ChallengeStatus;
  challenge: DrivingChallengeState;
}

/**
 * 한 렌더 프레임의 저빈도 텔레메트리 샘플링입니다.
 *
 * 주행 물리 자체는 1/120초로 적분되고, 그래프는 화면 픽셀보다 촘촘할 필요가 없어 10Hz로
 * 줄입니다. 배열은 ref 안에서만 변하고 완료 시에만 불변 복사본을 공개합니다.
 */
export function stepDrivingTelemetry(
  state: DrivingTelemetryState,
  frame: DrivingTelemetryFrame
): DrivingTelemetryState {
  const {
    previousVehicle,
    nextVehicle,
    input,
    dt,
    vehicleId,
    courseId,
    courseKind,
    sessionWasActive,
    completedLapsBefore,
    session,
    challengeId,
    challengeStatusBefore,
    challenge,
  } = frame;

  const activityChanged =
    state.vehicleId !== vehicleId
    || state.courseId !== courseId
    || state.challengeId !== challengeId;
  const spawnChanged = state.observedSpawnCount !== nextVehicle.spawnCount;
  if (activityChanged) {
    state.vehicleId = vehicleId;
    state.courseId = courseId;
    state.challengeId = challengeId;
    state.observedSpawnCount = nextVehicle.spawnCount;
    state.course = createChannel();
    state.challenge = createChannel();
    // 다른 차/코스/챌린지 아래에 이전 결과를 보여 주지 않습니다.
    state.completedRun = null;
    state.revision += 1;
  } else if (spawnChanged) {
    state.observedSpawnCount = nextVehicle.spawnCount;
    state.course = createChannel();
    state.challenge = createChannel();
    // 같은 활동의 재센터는 최근 완료 그래프를 보존하고 진행 중 trace만 버립니다.
  }

  if (!state.course.active && !sessionWasActive && session.runActive) {
    state.course = beginChannel(previousVehicle, input);
  }
  if (
    challengeId !== 'free-drive'
    && !state.challenge.active
    && challengeStatusBefore !== 'running'
    && challenge.status === 'running'
  ) {
    state.challenge = beginChannel(previousVehicle, input);
  }

  advanceChannel(state.course, dt, nextVehicle, input);
  advanceChannel(state.challenge, dt, nextVehicle, input);

  if (session.completedLaps > completedLapsBefore && session.lastTimeSeconds !== null) {
    publishRun(
      state,
      'course',
      courseId,
      session.lastTimeSeconds,
      state.course,
      nextVehicle,
      input
    );
    // 서킷은 결승점이 다음 랩의 시작점입니다. 스프린트는 완주 상태에서 멈춥니다.
    state.course = courseKind === 'circuit'
      ? beginChannel(nextVehicle, input)
      : createChannel();
  }

  if (
    challengeStatusBefore !== 'complete'
    && challenge.status === 'complete'
    && challenge.resultValue !== null
  ) {
    publishRun(
      state,
      'challenge',
      challengeId,
      challenge.resultValue,
      state.challenge,
      nextVehicle,
      input
    );
    state.challenge = createChannel();
  } else if (challenge.status === 'invalid') {
    state.challenge = createChannel();
  }

  return state;
}
